import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "@/components/site/Page";
import { pathFor } from "@/lib/i18n";

const TITLE = "Asesoría antes de la mudanza – Swedish International School";
const DESCRIPTION =
  "Incluida si lo deseáis para familias que inscriben a un alumno a jornada completa: una sesión de 45 minutos sobre el inicio del curso y la mudanza a España.";

export const Route = createFileRoute("/es/asesoria-de-reubicacion")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/es/asesoria-de-reubicacion") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "es_ES" },
      ...ogImage(),
    ],
    links: seoLinks("/es/asesoria-de-reubicacion"),
  }),
  component: RelocationPage,
});

function RelocationPage() {
  return (
    <>
      <PageHero
        eyebrow="Incluida en la inscripción"
        title="Una sesión de 45 minutos antes de la mudanza"
        intro="La sesión está incluida si lo deseáis y se aplica a las familias que inscriben al menos a un alumno a jornada completa durante al menos un trimestre. Cuando habéis sido admitidos y la matrícula está pagada, invitamos a la familia a una sesión personal de unos 45 minutos donde repasamos el inicio escolar y lo práctico de la vida en la Costa del Sol."
      />

      <Prose>
        <p>
          Mudarse al extranjero con niños implica muchas decisiones en poco tiempo. Llevamos muchos
          años recibiendo a familias de toda Escandinavia y vemos que las mismas preguntas se
          repiten. Por eso ofrecemos a cada familia nueva una sesión donde podéis plantear vuestras
          dudas a alguien que conoce tanto el colegio como la zona.
        </p>

        <h2>Qué repasamos</h2>
        <p>
          Repasamos lo práctico que conviene tener en cuenta antes de la mudanza y lo que os
          espera una vez instalados, y respondemos a vuestras preguntas sobre todo, desde el
          inicio escolar hasta la vida cotidiana. Lo que aborda la sesión depende siempre de
          vuestra situación – no hay dos familias iguales.
        </p>

        <h2>Cómo funciona</h2>
        <ul>
          <li>
            Incluida si lo deseáis, para familias con al menos un alumno a jornada completa durante
            al menos un trimestre – no se incluye en estancias cortas.
          </li>
          <li>
            Marcáis la casilla en el formulario de inscripción para solicitar la sesión y nosotros
            nos ponemos en contacto.
          </li>
          <li>Se realiza como pronto 15 días después de firmar el contrato y completar el pago.</li>
          <li>Unos 45 minutos, presencial en el colegio o en línea.</li>
          <li>Una vez por familia – independientemente de cuántos hijos inscribáis.</li>
        </ul>

        <h2>Conviene saber</h2>
        <p>
          La sesión es una orientación general del colegio basada en nuestra experiencia recibiendo
          a familias recién llegadas. No sustituye el asesoramiento jurídico, fiscal o financiero y
          no gestionamos viviendas. Si necesitáis apoyo especializado, podemos orientaros.
        </p>

        <p>
          <Link className="text-primary link-quiet" to={pathFor("/ansokan", "es") as never}>
            Hacer una inscripción
          </Link>{" "}
          o{" "}
          <Link className="text-primary link-quiet" to={pathFor("/avgifter", "es") as never}>
            leer más sobre las tarifas
          </Link>
          .
        </p>
      </Prose>

      <div className="pb-16 sm:pb-24">
        <CtaBand
          title="¿Queréis la sesión?"
          text="Marcad la casilla en el formulario de inscripción y reservamos una hora cuando hayáis sido admitidos. Se aplica a familias con al menos un alumno a jornada completa, durante al menos un trimestre."
          locale="es"
        />
      </div>
    </>
  );
}
